<template>
  <!-- Tiles carry the tag number, a short machine title and a profile picture of the machine,
       and nothing else - no owner, hours, serial or history. A tile is how someone finds a
       machine. Everything else is behind the PIN.

       The picture is a PUBLISHING DECISION, not decoration. The directory is public, so every
       badge here is readable by anyone who loads the page. Only machines whose picture has been
       deliberately placed in assets/machine-badges/ get one, and each file is a cropped profile
       shot with its metadata stripped - no EXIF, no GPS, no timestamp. A machine with no file
       simply renders without a badge; the example machines on the explainer page have none.

       Shared by the maintained-units directory (real machines) and the explainer page
       (example machines) so the two grids cannot drift apart.

       Machines that will never carry a tag - an RV, most road trucks - appear as generic tiles
       carrying only what the machine IS ("Skid steer", "RV"). They deliberately link nowhere:
       there is no public key to open them with, and their owners reach them through a private
       per-machine link instead. Publishing the type and nothing else is what lets the directory
       show the real shape of what Centerfire maintains without naming anyone's equipment.

       Sale units do NOT use this component. They are found by machine, not by number -
       see ListingTiles.vue. -->
  <div class="unit-grid" :aria-label="ariaLabel">
    <component
      :is="tile.lookupNumber ? 'router-link' : 'div'"
      v-for="(tile, index) in tiles"
      :key="tile.lookupNumber || `generic-${index}`"
      class="unit-card"
      :class="{ 'has-badge': badges[tile.lookupNumber], generic: !tile.lookupNumber }"
      v-bind="tile.lookupNumber ? { to: `/maintunits/${tile.lookupNumber}` } : {}"
    >
      <template v-if="tile.lookupNumber">
        <img
          v-if="badges[tile.lookupNumber]"
          class="tile-badge"
          :src="badges[tile.lookupNumber]"
          alt=""
          decoding="async"
        />
        <span class="tile-label">CFM#</span>
        <strong class="tile-number">{{ tile.lookupNumber }}</strong>
        <!-- A marker, not a link: the card is already an <a> to the record gate, and an <a>
             inside an <a> is invalid HTML that browsers split unpredictably. The listing is
             one nav click away in Center FireSale, which is where a buyer is headed anyway. -->
        <span v-if="tile.forSale" class="tile-sale">For sale</span>
        <span v-if="tile.badge" class="tile-demo">{{ tile.badge }}</span>
        <span v-if="tile.label" class="tile-type">{{ tile.label }}</span>
        <span class="record-link">{{ linkText }} <span aria-hidden="true">→</span></span>
      </template>
      <template v-else>
        <span class="tile-label">Maintained</span>
        <strong class="tile-generic">{{ tile.publicType }}</strong>
        <span class="tile-type">Reached by a private link, not a scan.</span>
      </template>
    </component>
  </div>
</template>

<script>
// Which machines have a picture is settled at BUILD time by what is in the folder, keyed by
// tag. Fetching /machine-badges/<tag>.jpg and hiding the image on a 404 was tried first and is
// worse in two ways: with lazy loading the 404 does not arrive until the tile scrolls into
// view, so a badge-less tile sits holding a 96px gap and then reflows when the error lands;
// and every example machine fires a console 404 on a page we ask prospects to look at.
const badgeFiles = require.context('@/assets/machine-badges', false, /^\.\/\d{3,8}\.jpg$/);
const BADGES = badgeFiles.keys().reduce((map, key) => {
  map[key.replace(/^\.\/|\.jpg$/g, '')] = badgeFiles(key);
  return map;
}, {});

export default {
  name: 'MachineTiles',
  props: {
    tiles: { type: Array, required: true },
    ariaLabel: { type: String, default: 'Machine directory' },
    linkText: { type: String, default: 'Open protected record' },
  },
  data() {
    return { badges: BADGES };
  },
};
</script>

<style scoped>
.unit-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.unit-card {
  /* Named so the badge and the min-height below cannot drift apart - they are two halves of
     one measurement, and the whole bug this fixes came from them being written twice. */
  --pad: clamp(1.2rem, 2.5vw, 1.8rem);
  --badge-size: clamp(76px, 6.6vw, 88px);
  --badge-top: clamp(0.85rem, 1.7vw, 1.2rem);
  --badge-ring: 2px;

  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: var(--pad);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: var(--radius);
  background: linear-gradient(145deg, rgba(255, 90, 2, 0.065), transparent 48%);
  transition: border-color 0.3s ease, transform 0.35s ease, background-color 0.3s ease;
}

/* The badge file is a square JPEG with the circle inscribed, so border-radius clips the
   corners away entirely - no alpha channel needed, and a fifth of the weight of a PNG.
   The ring is drawn here rather than baked into the file, which keeps one master usable at
   any weight or colour.

   It sits above the text's own top padding on purpose: the tile reads better with the circle
   tucked nearer the corner than aligned to the "CFM#" baseline. */
.tile-badge {
  position: absolute;
  top: var(--badge-top);
  right: var(--badge-top);
  width: var(--badge-size);
  height: var(--badge-size);
  border-radius: 50%;
  border: var(--badge-ring) solid var(--white);
  object-fit: cover;
}

/* The badge is out of flow, so nothing in the tile knows it is there. The orange rule above
   the record link sits wherever the text above it ends - and whether the machine type wraps
   to two lines depends on the tile's width, which changes with the viewport. At the widths
   where it stayed on one line the rule rode up under the circle.

   Pushing the link to the bottom and giving the card a floor tall enough for the badge fixes
   it for every width at once, rather than tuning a number per breakpoint. */
.has-badge {
  min-height: calc(
    var(--badge-top) + var(--badge-size) + (2 * var(--badge-ring))
    + 0.85rem                                   /* clearance under the circle */
    + 2.05rem                                   /* the record link and its rule */
    + var(--pad)
  );
}

/* A generic tile is a statement, not a door. Nothing to hover, nothing to open. */
.generic {
  background: none;
  border-style: dashed;
  border-color: rgba(255, 255, 255, 0.16);
}

.tile-generic {
  display: block;
  margin-top: 0.35rem;
  font-size: clamp(1.3rem, 2.6vw, 1.7rem);
  font-weight: 400;
  line-height: 1.15;
}

.unit-card:not(.generic):hover,
.unit-card:not(.generic):focus-visible {
  border-color: var(--orange);
  background-color: rgba(255, 90, 2, 0.045);
  transform: translateY(-2px);
}

.tile-label {
  display: block;
  font-size: 0.68rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  opacity: 0.7;
}

.tile-number {
  display: block;
  margin-top: 0.3rem;
  color: var(--orange);
  font-size: clamp(2.6rem, 5vw, 3.6rem);
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0.06em;
}

.tile-demo {
  display: inline-block;
  margin-top: 0.7rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.75;
}

/* Same shape as the demo chip so the tile vocabulary stays one thing, but in the accent
   colour: "for sale" is the one state a passer-by should notice at grid distance. */
.tile-sale {
  display: inline-block;
  align-self: flex-start;
  margin-top: 0.7rem;
  padding: 0.2rem 0.5rem;
  border: 1px solid var(--orange);
  color: var(--orange);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* Small print, matched to the label above the number rather than competing with it. The
   divider below is pulled in tight so it reads as an underline on this line. */
.tile-type {
  display: block;
  margin-top: 0.55rem;
  margin-bottom: 0.3rem;
  font-size: 0.68rem;
  letter-spacing: 0.03em;
  line-height: 1.35;
  opacity: 0.72;
}

/* Only the wrapping lines need to clear the badge. The tag number is four characters and
   stops well short of it, and the record link sits below the badge's bottom edge. Keying
   this off the class rather than applying it always means a tile with no picture - the
   example machines - uses its full width instead of reserving a gap for nothing. */
.has-badge .tile-demo,
.has-badge .tile-type {
  padding-right: calc(var(--badge-size) + 1.1rem);
}

/* margin-top: auto rather than a fixed gap - the card is a flex column, so this is what
   holds the rule at the bottom and lets the badge's min-height open the space above it.
   The old 0.3rem gap moved onto .tile-type's margin-bottom so a packed tile keeps it. */
.record-link {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: auto;
  padding-top: 0.55rem;
  border-top: 1px solid rgba(255, 90, 2, 0.55);
  color: var(--orange);
  font-weight: 500;
}

@media (max-width: 900px) {
  .unit-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  .unit-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
